import { type ChatContextParams } from '@myre/app-agent-generated/types/ChatContextParams';
import { type Translated } from '@myre/common-i18n';
import { type UiLibI18n } from '@myre/domain-i18n-wordings';

import {
  type ChatContextItem,
  type ChatContextKey,
} from '../interfaces/chat.interfaces';
import { type ChatContextName } from '../services/chat-context-names.service';

type ChatWordings = UiLibI18n['chat'];

type ChatContextWordings = ChatWordings['context'];

/** The product's name for the entity each context id points at. */
const CONTEXT_KEY_WORDINGS: Readonly<
  Record<ChatContextKey, keyof ChatContextWordings>
> = {
  acquisitionId: 'acquisition',
  budgetId: 'budget',
  budgetPlanId: 'budgetPlan',
  capexBudgetYear: 'capexBudgetYear',
  capexPackageId: 'capexPackage',
  capexProgramId: 'capexProgram',
  consolidationId: 'consolidation',
  contractId: 'contract',
  corporateId: 'corporate',
  covenantId: 'covenant',
  debtId: 'debt',
  equityId: 'equity',
  invoiceId: 'invoice',
  lenderAmountId: 'lenderAmount',
  lenderId: 'lender',
  portfolioId: 'portfolio',
  propertyChargeId: 'propertyCharge',
  propertyId: 'realEstateAsset',
  tenantGroupId: 'tenantGroup',
  tenantId: 'tenant',
  trancheId: 'tranche',
  trueUpId: 'trueUp',
  valuationId: 'valuation',
};

/**
 * The router serializes every param of the route, so a key read off the
 * context is any string: only the ids of the agent's contract scope anything.
 */
export function isChatContextKey(key: string): key is ChatContextKey {
  return Object.hasOwn(CONTEXT_KEY_WORDINGS, key);
}

/** Names the entity a context id points at. */
export function contextKeyLabel(
  key: ChatContextKey,
  wordings: ChatContextWordings,
): Translated {
  return wordings[CONTEXT_KEY_WORDINGS[key]]();
}

/**
 * Builds the chips of the context zone: one per id in scope, carrying the
 * entity name when the app resolved it, ordered from the broadest scope to the
 * entity the page is about.
 */
export function buildChatContextItems(params: {
  contextParams: Readonly<Partial<ChatContextParams<string>>>;
  names: Readonly<Record<string, ChatContextName | undefined>>;
  excludedKeys: readonly string[];
  /** The ids of the active route, root first — see `routeParamsPath`. */
  keyPath: readonly string[];
  label: (key: ChatContextKey) => Translated;
}): ChatContextItem[] {
  const { contextParams, names, excludedKeys, keyPath, label } = params;
  const items: ChatContextItem[] = [];

  for (const [key, value] of Object.entries(contextParams)) {
    if (typeof value !== 'string' && typeof value !== 'number') {
      continue;
    }

    // A route param outside the contract is stripped by the agent API: it
    // scopes nothing, so no chip claims it does.
    if (!isChatContextKey(key) || excludedKeys.includes(key)) {
      continue;
    }

    const resolved = names[key];

    items.push({
      key,
      value,
      label: label(key),
      // A name resolved for another id means the store still lags behind the
      // route: show the id rather than the name of the previous entity.
      name:
        resolved && String(resolved.id) === String(value)
          ? resolved.name
          : undefined,
    });
  }

  return items.sort(
    (left, right) =>
      contextKeyDepth(left.key, keyPath) - contextKeyDepth(right.key, keyPath),
  );
}

/** One line of the folded tooltip: the entity named, or its bare id. */
export function contextItemText(
  item: ChatContextItem,
  wordings: ChatWordings,
): Translated {
  if (item.name === undefined) {
    return wordings.contextItemId({
      bindings: { label: item.label, id: item.value },
    });
  }

  return wordings.contextItemNamed({
    bindings: { label: item.label, name: item.name },
  });
}

/** An id the route does not carry hangs off no page: it sorts deepest. */
function contextKeyDepth(key: string, keyPath: readonly string[]): number {
  const depth = keyPath.indexOf(key);

  return depth === -1 ? keyPath.length : depth;
}
